<script setup>
import { computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';


const props = defineProps({
    setting: Object,
    jenisMobil: Object,
    relatedJenisMobil: Array,
    relatedMobils: Array,
    firstVehicle: Object, // For SEO Image if passed
    processedArtikel: String,
    processedDeskripsi: String,
    // SEO Data (merged)
    title: String,
    meta_description: String,
    meta_keywords: String,
    og_image: String,
    og_title: String,
    og_description: String,
    canonical_url: String,
    twitter_title: String,
    twitter_description: String,
    twitter_image: String
});


// WA Logic for booking buttons
const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const formatCurrency = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

// Helper for booking URL
const getBookingUrl = (mobil) => {
    const text = encodeURIComponent(`Halo, saya tertarik untuk booking ${mobil.mobil_nama}. Mohon informasi lebih lanjut.`);
    return `https://wa.me/${wa.value}?text=${text}`;
};

// Article Content with HTML (Already processed by backend? Controller says: processedArtikel IS implemented for detail, but for this 'artikel' method, let's check controller.)
// Controller 'artikel' method:
// It passes 'jenisMobil' which has 'jenis_artikel' (raw?).
// The Blade template uses `\App\Services\AutoLinkService::autoLinkAll` call inside the view. 
// We should probably move that logic to the Controller to pass a cleaner HTML string like `processedArtikel`.
// BLADE: {!! \App\Services\AutoLinkService::... !!}
// ACTION: I will update controller to pass 'processedContent' prop. For now, let's assume props.processedContent or we render `jenisMobil.jenis_artikel` raw if controller not updated yet (but plan to update it).
// Actually, looking at controller, it DOES NOT process it yet. I need to update controller.

// JSON-LD Schema
const jsonLd = computed(() => {
    const appUrl = usePage().props.app_url || window.location.origin;
    const currentUrl = props.canonical_url || window.location.href;
    const logoUrl = props.setting?.setting_logo ? `/storage/logo/${props.setting.setting_logo}` : '/front/img/logo.png';

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Article",
                "headline": props.jenisMobil.jenis_nama,
                "description": props.meta_description,
                "image": props.og_image,
                "datePublished": props.jenisMobil.created_at,
                "dateModified": props.jenisMobil.updated_at,
                "author": {
                    "@type": "Organization",
                    "name": props.setting?.setting_title || 'Gotransport Banyuwangi',
                    "url": appUrl
                },
                "publisher": {
                    "@type": "Organization",
                    "name": props.setting?.setting_title || 'Gotransport Banyuwangi',
                    "url": appUrl,
                    "logo": {
                        "@type": "ImageObject",
                        "url": `${appUrl}${logoUrl}`
                    }
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": currentUrl
                }
            },
            {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { 
                        "@type": "ListItem", 
                        "position": 1, 
                        "name": "Beranda", 
                        "item": appUrl 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 2, 
                        "name": "Mobil", 
                        "item": `${appUrl}/daftar-armada` 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 3, 
                        "name": props.jenisMobil.jenis_nama, 
                        "item": currentUrl 
                    }
                ]
            }
        ]
    };
});
</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="title" 
        :metaDescription="meta_description"
        :og_title="og_title || title"
        :og_description="og_description || meta_description"
        :og_image="og_image"
        :meta_keywords="meta_keywords"
    >
        <Head>

            <meta name="description" :content="meta_description" />
            <meta v-if="meta_keywords" name="keywords" :content="meta_keywords" />
            <link rel="canonical" :href="canonical_url || route('mobil.jenis.artikel', jenisMobil.jenis_slug)" />
            
            <!-- Schema.org JSON-LD -->
            <component :is="'script'" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
        </Head>

        <!-- Hero Section -->
        <PageHero 
            :title="jenisMobil.jenis_nama"
            :subtitle="`Panduan lengkap tentang Jenis Mobil ${jenisMobil.jenis_nama} untuk membantu Anda memahami karakteristik, keunggulan, dan tips memilih kendaraan yang tepat.`"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Mobil', url: '/daftar-armada' },
                { label: jenisMobil.jenis_nama }
            ]"
        />

        <!-- Content -->
        <section class="py-8 md:py-10 bg-white dark:bg-gray-900 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                        <article class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl shadow-sm transition-colors duration-300">
                            <div class="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                                <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <i class="fa fa-car text-blue-600"></i>
                                    {{ jenisMobil.jenis_nama }}
                                </h2>
                            </div>

                            <!-- Article Content -->
                            <!-- Prioritize Article, Fallback to Description in Blue Box -->
                            <div v-if="processedArtikel" class="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed mb-6 prose-headings:text-gray-900 dark:prose-headings:text-white prose-strong:text-gray-900 dark:prose-strong:text-white" v-html="processedArtikel"></div>
                            
                            <div v-else class="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-500 p-4 mb-6">
                                <p class="font-bold text-gray-900 dark:text-white mb-2">{{ jenisMobil.jenis_nama }}</p>
                                <div v-if="processedDeskripsi" class="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300" v-html="processedDeskripsi"></div>
                                <p v-else class="text-sm text-gray-600 dark:text-gray-400 mb-0">
                                    Artikel untuk jenis mobil ini sedang dalam proses penulisan. Silakan kembali lagi nanti untuk membaca konten lengkapnya.
                                </p>
                            </div>
                        
                             <!-- Vehicle Type Information -->
                             <div class="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-500 p-4 mb-6 rounded-r-lg">
                                <div class="flex items-start gap-3">
                                    <i class="fa fa-info-circle text-blue-600 dark:text-blue-400 mt-1 text-lg"></i>
                                    <p class="text-sm text-gray-700 dark:text-gray-300 m-0">
                                        Jenis kendaraan ini merupakan bagian dari armada rental Gotransport yang siap melayani
                                        kebutuhan perjalanan Anda dengan kualitas terbaik.
                                    </p>
                                </div>
                            </div>
                            
                            <!-- Social Share -->
                            <div class="mb-8">
                                <SocialShare :url="canonical_url" :title="title" />
                            </div>

                            <!-- CTA -->
                            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-8 text-center rounded-xl">
                                <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                    Tertarik dengan {{ jenisMobil.jenis_nama }}?
                                </h3>
                                <p class="text-gray-600 dark:text-gray-300 mb-6">
                                    Lihat semua kendaraan {{ jenisMobil.jenis_nama }} yang tersedia untuk disewa
                                </p>
                                <Link :href="`/mobil/jenis/${jenisMobil.jenis_slug}`"
                                   class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-md hover:shadow-lg transition-all">
                                    <i class="fa fa-car"></i>
                                    <span>Lihat Armada {{ jenisMobil.jenis_nama }}</span>
                                </Link>
                            </div>
                        </article>
                    </div>

                    <!-- Sidebar -->
                    <aside class="lg:col-span-1 space-y-6">
                        <!-- Armada Widget -->
                        <div v-if="relatedMobils && relatedMobils.length > 0">
                             <h4 class="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 text-xl">
                                <i class="fa fa-car text-orange-500"></i>
                                Armada {{ jenisMobil.jenis_nama }}
                             </h4>
                             <div class="space-y-6">
                                <div v-for="mobil in relatedMobils" :key="mobil.id" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group">
                                    <!-- Image Area -->
                                    <div class="relative bg-white dark:bg-gray-700 aspect-[4/3] border-b border-gray-100 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                                        <div v-if="mobil.jenis_mobil" class="absolute top-3 left-3 bg-blue-600 text-white text-[10px] uppercase font-bold px-3 py-1 rounded shadow-sm z-10">
                                            {{ mobil.jenis_mobil.jenis_nama }}
                                        </div>
                                        <Link :href="`/mobil/${mobil.mobil_slug}`" class="w-full h-full flex items-center justify-center">
                                            <img :src="mobil.mobil_foto ? `/storage/mobil/${mobil.mobil_foto}` : '/front/img/default-car.jpg'"
                                                :alt="mobil.mobil_nama" 
                                                class="max-w-full max-h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500">
                                        </Link>
                                    </div>

                                    <div class="p-4">
                                        <!-- Title -->
                                        <h5 class="font-bold text-gray-900 dark:text-white text-lg mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                            <Link :href="`/mobil/${mobil.mobil_slug}`">
                                                {{ mobil.mobil_nama }}
                                            </Link>
                                        </h5>
                                        <!-- Desc (Optional, if available in object or omit if data missing) -->
                                        
                                        <!-- Pricing Rows -->
                                        <div class="space-y-2 mb-4 mt-4">
                                            <div v-if="mobil.mobil_harga_sewa" class="flex items-center justify-between text-xs bg-orange-50 dark:bg-gray-700 border-l-4 border-orange-500 p-2 rounded-r">
                                                <div class="flex items-center gap-2">
                                                    <div class="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-white"><i class="fa fa-key text-[10px]"></i></div>
                                                    <span class="text-gray-700 dark:text-gray-300 font-medium">Lepas Kunci</span>
                                                </div>
                                                <span class="font-bold text-orange-700 dark:text-orange-400">Rp {{ formatCurrency(mobil.mobil_harga_sewa) }}</span>
                                            </div>
                                            <div v-if="mobil.mobil_harga_include" class="flex items-center justify-between text-xs bg-blue-50 dark:bg-gray-700 border-l-4 border-blue-500 p-2 rounded-r">
                                                <div class="flex items-center gap-2">
                                                    <div class="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white"><i class="fa fa-user text-[10px]"></i></div>
                                                    <span class="text-gray-700 dark:text-gray-300 font-medium">Dengan Sopir</span>
                                                </div>
                                                <span class="font-bold text-blue-700 dark:text-blue-400">Rp {{ formatCurrency(mobil.mobil_harga_include) }}</span>
                                            </div>
                                        </div>

                                        <!-- Booking Button -->
                                        <a :href="getBookingUrl(mobil)" target="_blank" class="block w-full text-center bg-green-500 hover:bg-green-600 text-white text-sm font-bold py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg">
                                            <i class="fa fa-whatsapp mr-1.5 text-lg"></i> Booking
                                        </a>
                                    </div>
                                </div>
                             </div>
                        </div>

                        <!-- Related Types -->
                         <div v-if="relatedJenisMobil && relatedJenisMobil.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm transition-colors">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 pb-3">
                                <i class="fa fa-tags text-blue-500"></i>
                                Jenis Lainnya
                             </h3>
                             <div class="space-y-2">
                            <Link v-for="related in relatedJenisMobil" :key="related.id" 
                                   :href="`/mobil/jenis/${related.jenis_slug}/artikel`"
                                   class="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 group transition-colors">
                                    <span class="text-sm text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 font-medium flex items-center gap-2">
                                         <i class="fa fa-angle-right text-gray-400 group-hover:text-blue-500"></i>
                                         {{ related.jenis_nama }}
                                     </span>
                                 </Link>
                             </div>
                         </div>
                    </aside>
                </div>
            </div>
        </section>

    </FrontLayout>
</template>
